<template>
  <div>
    <div class="">
      <div>
        <div class="margin-top-1">
          <div class="form-holder">
            <div class="form-label">
              <p><strong>Card Name</strong></p>
              <span>Required</span>
            </div>
            <div class="form-input form-input-value">
              <div class="form-input-value input-value">
                <input
                  type="text"
                  placeholder="Enter name"
                  v-model="cardDetails.name"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="margin-top-2">
          <div class="form-holder">
            <div class="form-label">
              <p><strong>Value (USD)</strong></p>
              <span>Required</span>
            </div>
            <div class="form-input form-input-value">
              <div class="form-input-value input-value credit-control">
                <input
                  placeholder="$0.00"
                  v-money="cardValue"
                  v-model.lazy="cardDetails.value"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="margin-top-2">
          <div class="form-holder">
            <div class="form-label">
              <p><strong>Expiration Date</strong></p>
            </div>
            <div class="form-input form-input-value">
              <div class="form-input-value custom-date-input flex">
                <input
                  ref="customMonthInput"
                  type="number"
                  placeholder="MM"
                  v-model="customInput.month"
                  class="text-center"
                  @input="onCustomDateInput($event, 'month')"
                />
                <span class="divider">/</span>
                <input
                  ref="customDayInput"
                  type="number"
                  placeholder="DD"
                  v-model="customInput.day"
                  @input="onCustomDateInput($event, 'day')"
                  class="text-center"
                />
                <span class="divider">/</span>
                <input
                  ref="customYearInput"
                  type="number"
                  placeholder="YYYY"
                  v-model="customInput.year"
                  @input="onCustomDateInput($event, 'year')"
                  class="text-center"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="margin-top-2">
          <div class="form-holder">
            <div class="form-label">
              <p><strong>Email(s)</strong></p>
            </div>
            <div class="form-input">
              <div class="allow-domains" v-if="!file">
                <input-tag
                  class="is-white is-muted"
                  :class="{ 'updating-tags': false }"
                  :validate="validateEmail"
                  placeholder="Enter email"
                  v-model="cardDetails.emails"
                  :add-tag-on-blur="true"
                ></input-tag>
              </div>
              <div v-else class="flex flex-col file-details">
                <div class="flex gap file-name-holder">
                  <p>{{ file.name }}</p>
                  <button @click="clearFileUpload">x</button>
                </div>
                <p class="file-count">({{ emailCount }} emails found)</p>
              </div>
              <div
                class="upload-option-holder"
                v-if="!file"
                :class="{ muted: value || card_emails.length }"
              >
                <span class="option-text margin-top-2">or</span>
                <div class="flex upload-button-holder gap">
                  <UploadButton
                    class="upload-button"
                    :isDisabled="Boolean(card_emails.length)"
                    @import="handleImport"
                  />
                  <span class="option-desc"
                    >Upload a comma-separated value (CSV) file containing only
                    emails of recipients.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="margin-top-3 restrict-cards">
          <div class="flex items-end">
            <label class="checkbox-container flex">
              <input
                type="checkbox"
                :checked="cardDetails.restrictEmails"
                @click.stop="
                  cardDetails.restrictEmails = !cardDetails.restrictEmails
                "
              />
              <span class="checkmark checkmark-center"></span>
              <div class="flex">
                <p
                  class="flex gap-1"
                  style="margin-left: 1.6rem; margin-right: 0.2rem;"
                >
                  <strong> Restrict to email(s)</strong>
                  <span class="tool-tip flex items-center">
                    <Tooltip
                      :popOverContent="restrictEmailsTooltip"
                      popOverPosition="right"
                    />
                  </span>
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>
      <div class="cta margin-top-3  margin-bottom-1 flex justify-end gap-3">
        <div class="btn-save">
          <button
            @click="onCreateGiftCard"
            class="button-gemnote button-gemnote--small"
            :disabled="isDisabled"
          >
            <span>Save </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InputTag from 'vue-input-tag'
import UploadButton from '../UploadGiftCardsButton.vue'
import i18n from '@/internatinalization/i18n'
import Tooltip from '@/components/Universal/ToolTipInfo.vue'
import { VMoney } from 'v-money'
import { countRowsInCSV } from '../../../helpers'
export default {
  name: 'EcommerceGiftCardCreate',
  directives: { money: VMoney },
  data() {
    return {
      cardValue: {
        decimal: '.',
        thousands: ',',
        precision: 2,
        prefix: '$',
        masked: false
      },
      currentStep: 1,
      card_emails: [],
      value: '',
      file: false,
      customInput: {
        month: '',
        day: '',
        year: ''
      },
      emailCount: '',
      cardDetails: {
        name: '',
        value: 0,
        expirationDate: '',
        emails: [],
        restrictEmails: false,
        file: null
      }
    }
  },
  components: {
    InputTag,
    UploadButton,
    Tooltip
  },

  computed: {
    restrictEmailsTooltip() {
      return `<span><b>Select this option to restrict gift card redemption to only emails listed</b> (Gift cards cannot be transferred or redeemed by members whose emails are not listed).</span> `
    },
    isDisabled() {
      if (this.currentStep <= 1) {
        // Disable if no name or value is provided
        if (!this.cardDetails.name || !this.cardDetails.value) {
          return true
        }

        // Disable if a file is selected but emailCount is 0 (no emails in the CSV)
        if (this.file && this.emailCount === 0) {
          return true
        }

        return false
      } else {
        return false
      }
    }
  },

  methods: {
    onCustomDateInput(e, type) {
      let value = e.target.value
      switch (type) {
        case 'month':
          this.customInput.month = value.slice(0, 2)
          if (value.length === 2 && Number(value) < 13) {
            this.customInput.month = value.slice(0, 2)
            this.$refs.customDayInput.focus()
            this.setExpirationDate()
          }
          break
        case 'day':
          this.customInput.day = value.slice(0, 2)
          if (value.length === 2 && Number(value) < 32) {
            this.customInput.day = value.slice(0, 2)
            this.$refs.customYearInput.focus()
            this.setExpirationDate()
          }
          break
        case 'year':
          this.customInput.year = value.slice(0, 4)
          if (value.length === 4) {
            this.customInput.year = value.slice(0, 4)
            this.$refs.customYearInput.blur()
            this.setExpirationDate()
          }
          break

        default:
          break
      }
    },
    isFutureDate() {
      const givenDate = new Date(
        `${this.customInput.month}/${this.customInput.day}/${this.customInput.year}`
      )
      // check if date is invalid
      if (isNaN(givenDate)) {
        this.$store.dispatch('notification/pushNotification', {
          type: 'error',
          messages: ['Expiration date is not a valid date.']
        })
        return false
      }
      const currentDate = new Date()

      if (givenDate.getTime() > currentDate.getTime()) {
        return true
      } else {
        this.$store.dispatch('notification/pushNotification', {
          type: 'error',
          messages: ['Expiration date must be a future date.']
        })
        return false
      }
    },
    setExpirationDate() {
      this.cardDetails.expirationDate = `${this.customInput.day}/${this.customInput.month}/${this.customInput.year}`
    },
    validateEmail(value) {
      // eslint-disable-next-line no-useless-escape
      return /[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/.test(
        value
      )
    },
    clearFileUpload() {
      this.file = null
      this.cardDetails.file = undefined
      this.emailCount = 0
    },
    async handleImport(e, file) {
      //   this.file = file
      if (file) {
        this.cardDetails.file = file
        this.file = file
        const emailCount = await countRowsInCSV(file)
        this.emailCount = emailCount
      }
    },
    sendGiftCard(e, sendCard) {
      this.isModalActive = false
      this.$router.push({ name: 'EcommerceGiftCards' })

      if (sendCard) {
        this.$store.dispatch('notification/pushNotification', {
          type: 'success',
          messages: [i18n.t('ecommerce.action.gitfcards-sent-success')]
        })
      }
    },
    onCreateGiftCard() {
      console.log(this.cardDetails)
      if (this.cardDetails.expirationDate) {
        if (this.isFutureDate()) {
          this.$emit('onCompleteStep', this.cardDetails, 1, this.emailCount)
        } else {
          // this.$store.dispatch('notification/pushNotification', {
          //   type: 'error',
          //   messages: ['Expiration date must be a future date.']
          // })
        }
      } else {
        this.$emit('onCompleteStep', this.cardDetails, 1, this.emailCount)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/global.scss';

.justify-end {
  justify-content: end !important;
}

.trending-container {
  padding: 1.25rem;
  border: 1px solid $main-6;
  border-radius: 2px;
  margin-bottom: 2rem;
}

.trending-header {
  h4 {
    text-align: left;
    font-size: 1.25rem;
    margin-bottom: 1.5rem;
  }
}

h4 {
  font-family: $editorial;
  font-weight: normal;
}

.card-header {
  .editorial {
    font-size: 1.25rem;
  }

  p {
    font-size: 0.838rem;
  }
}

.card-info {
  text-align: left;
  font-size: 0.838rem;
}

.form-holder {
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  &.textarea {
    align-items: flex-start;

    .form-label {
      width: 20%;
    }
  }

  .form-label {
    width: 15%;
    display: flex;
    flex-direction: column;
    text-align: left;

    p {
      font-size: 0.838rem;
    }

    span {
      font-size: 0.708rem;
      color: rgba(35, 34, 32, 0.5);
    }
  }

  .form-input {
    width: 85%;

    .upload-option-holder {
      &.muted {
        .option-text,
        .option-desc {
          color: #a09c95;
        }
      }

      .option-text {
        display: block;
        margin: 1rem 0;
        text-align: left;
        padding: 0px 5rem;
      }

      .upload-button-holder {
        display: flex;
        align-items: center;
        gap: 1rem;

        .option-desc {
          font-size: 0.773rem;
          font-weight: normal;
        }
      }
    }

    .input-value {
      width: 22%;
    }

    &.textarea-input {
      width: 100%;

      .input-value {
        width: 80% !important;
      }
    }

    input {
      font-weight: 400 !important;
      height: max-content;
    }

    .custom-date-input {
      gap: 0.3rem;
      width: 20%;

      input {
        font-weight: 400 !important;
        height: max-content;
      }

      .divider {
        font-weight: 400;
        color: #232220;
        font-size: 0.9rem;
      }
    }

    .file-details {
      align-items: flex-start;

      .file-name-holder {
        gap: 0.6rem;
        align-items: center;
        font-weight: 600;

        p {
          font-size: 0.838rem;
        }

        button {
          font-size: 0.838rem;
        }
      }

      .file-count {
        font-size: 0.708rem;
        color: #232220;
      }
    }
  }
}

.textarea-input {
  textarea {
    background-color: transparent;
    border: 1px solid #9b9b9b;
    outline: none !important;
    width: 100%;
    padding: 8px;
    font-size: 0.838rem;

    &:focus {
      border: 1px solid #9b9b9b;
    }
  }
}

.restrict-cards {
  p {
    font-size: 0.838rem;
  }
}

.allow-domains .vue-input-tag-wrapper {
  min-width: 40%;
  max-width: max-content;
}

.cta {
  text-align: right;

  &.top-spacing {
    margin-top: 7rem;
  }

  .button-gemnote--small {
    width: auto !important;
    padding: 0 1.5rem;
    height: 2.5rem;
    display: flex;
    margin-left: auto;
    gap: 20px;

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      background: #a09c95;
      color: #f5f2ee;
      border: none;
    }
  }
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}

@media (max-width: 425px) {
  .form-holder .form-label {
    width: 30%;
  }

  .form-holder .form-input .upload-option-holder .upload-button-holder {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }
}
</style>
